import {
  infiniteQueryOptions as baseInfiniteQueryOptions,
  queryOptions as baseQueryOptions,
} from "@tanstack/react-query";

import type { Promisable } from "@core/foundation/promise";
import type {
  CursorPaginatedResponse,
  CursorPagination,
} from "@core/ports/http";

// oxlint-disable-next-line @typescript-eslint/explicit-function-return-type
export function cursorPaginatedQueryOptions<Args, Item>({
  queryFn,
  queryKey: { group, variant = () => [] },
  refetchInterval,
}: {
  readonly queryFn: (
    args: Args & CursorPagination,
  ) => Promisable<CursorPaginatedResponse<Item>>;
  readonly queryKey: {
    readonly group: (args: Args & CursorPagination) => readonly unknown[];
    readonly variant?: (args: Args & CursorPagination) => readonly unknown[];
  };
  readonly refetchInterval?: number;
}) {
  // oxlint-disable-next-line @typescript-eslint/explicit-function-return-type
  const factory = (args: Args & CursorPagination) => {
    const groupQueryKey = group(args);

    const queryKey = [...groupQueryKey, ...variant(args)] as const;

    const regular = baseQueryOptions({
      queryFn: async () => queryFn(args),
      queryKey,
      refetchInterval,
    });

    const initialPageParam: string | undefined = undefined;
    const infinite = baseInfiniteQueryOptions({
      getNextPageParam: (lastPage: CursorPaginatedResponse<Item>) =>
        lastPage.nextCursor,
      initialPageParam,
      queryFn: async ({ pageParam }) => queryFn({ ...args, cursor: pageParam }),
      queryKey: [...queryKey, "infinite"],
    });

    return Object.assign(regular, { groupQueryKey, infinite });
  };

  return factory;
}
